"use client";

import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { cn } from "@/lib/cn";
import { useAppShell } from "@/components/providers/app-providers";
import { DENSITY_MODES, type DensityMode } from "@/lib/types";

const LABEL: Record<DensityMode, string> = {
  operations: "Operations",
  standard: "Standard",
  immersive: "Immersive",
};

/**
 * Real, switchable density-mode control (docs/DESIGN_TOKENS.md → "Responsive
 * density modes"). Toggling sets `data-density` on <html>, which tokens.css
 * uses to change spacing/type scale at the shell level.
 */
export function DensityToggle({ className }: { className?: string }) {
  const { density, setDensity } = useAppShell();

  return (
    <ToggleGroup.Root
      type="single"
      value={density}
      onValueChange={(value) => value && setDensity(value as DensityMode)}
      aria-label="Density mode"
      className={cn(
        "inline-flex items-center gap-1 rounded-control border border-border-subtle bg-surface-secondary p-1",
        className,
      )}
    >
      {DENSITY_MODES.map((mode) => (
        <ToggleGroup.Item
          key={mode}
          value={mode}
          className={cn(
            "rounded-control px-2.5 py-1.5 text-xs font-bold text-text-secondary transition-colors",
            "data-[state=on]:bg-surface-primary data-[state=on]:text-brand-primary data-[state=on]:shadow-[var(--shadow-subtle)]",
          )}
        >
          {LABEL[mode]}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
